import React, { useState, useEffect } from 'react';
import { 
  ShieldCheck, 
  Settings, 
  Check, 
  X, 
  ChevronRight, 
  Info, 
  Lock, 
  BarChart3, 
  Target, 
  MapPin, 
  ExternalLink,
  RotateCcw
} from 'lucide-react';
import { 
  getStoredConsent, 
  saveConsent, 
  acceptAllCookies, 
  rejectAllCookies 
} from '../services/cookieConsentService';
import { COOKIE_CATEGORIES } from '../data/cookieInfo';
import { CookiePreferences } from '../types/cookieConsent';

export const CookieConsentBanner: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'categories' | 'policy'>('categories');
  
  // Local preferences state in modal
  const [prefs, setPrefs] = useState<{
    analytics: boolean;
    marketing: boolean;
    functional: boolean;
  }>({
    analytics: false,
    marketing: false,
    functional: false
  });

  useEffect(() => {
    const existing = getStoredConsent();
    if (!existing) {
      // Delay slightly for smooth entrance
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 700);
      return () => clearTimeout(timer);
    } else {
      setPrefs({
        analytics: existing.analytics,
        marketing: existing.marketing,
        functional: existing.functional
      });
    }
  }, []);

  // Listen for open trigger from footer or floating button
  useEffect(() => {
    const handleOpen = () => {
      const current = getStoredConsent();
      if (current) {
        setPrefs({
          analytics: current.analytics,
          marketing: current.marketing,
          functional: current.functional
        });
      }
      setIsModalOpen(true);
    };

    window.addEventListener('open_cookie_settings', handleOpen);
    return () => window.removeEventListener('open_cookie_settings', handleOpen);
  }, []);

  const handleAcceptAll = () => {
    acceptAllCookies();
    setPrefs({ analytics: true, marketing: true, functional: true });
    setIsVisible(false);
    setIsModalOpen(false);
  };

  const handleRejectAll = () => {
    rejectAllCookies();
    setPrefs({ analytics: false, marketing: false, functional: false });
    setIsVisible(false);
    setIsModalOpen(false);
  };

  const handleSaveCustom = () => {
    saveConsent({
      necessary: true,
      analytics: prefs.analytics,
      marketing: prefs.marketing,
      functional: prefs.functional
    });
    setIsVisible(false);
    setIsModalOpen(false);
  };

  const getCategoryIcon = (id: string) => {
    switch (id) {
      case 'necessary':
        return <Lock className="w-4 h-4 text-emerald-400" />;
      case 'analytics':
        return <BarChart3 className="w-4 h-4 text-[#009FE3]" />;
      case 'marketing':
        return <Target className="w-4 h-4 text-amber-400" />;
      case 'functional':
        return <MapPin className="w-4 h-4 text-[#C5A46E]" />;
      default:
        return <Info className="w-4 h-4 text-slate-400" />;
    }
  };

  return (
    <>
      {/* 1. Main Bottom Cookie Consent Banner (shown on first visit) */}
      {isVisible && !isModalOpen && (
        <div 
          role="region" 
          aria-label="Съгласие за използване на бисквитки"
          className="fixed bottom-0 inset-x-0 z-50 p-3 sm:p-5 animate-in fade-in slide-in-from-bottom-6 duration-300"
        >
          <div className="max-w-5xl mx-auto bg-[#0F1E32]/98 backdrop-blur-md text-white rounded-2xl p-5 sm:p-6 shadow-2xl border border-slate-700/80 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-5 font-sans">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-xl bg-[#C5A46E]/15 border border-[#C5A46E]/30 flex items-center justify-center shrink-0 mt-0.5 text-xl">
                🍪
              </div>
              <div className="space-y-1.5">
                <div>
                  <h3 className="font-bold text-sm sm:text-base text-white tracking-wide">
                    Управление на бисквитките & Поверителност
                  </h3>
                </div>
                <p className="text-xs sm:text-sm text-slate-300 leading-relaxed font-light">
                  Този уебсайт използва бисквитки в съответствие с европейското законодателство (GDPR и Директива за e-Privacy). Използваме ги за базова функционалност, статистически анализ (<strong>Google Analytics 4 & Search Console</strong>), измерване на реклами (<strong>Google Ads & Meta Pixel</strong>) и интерактивни карти (<strong>Google Maps</strong>).
                </p>
                <div className="flex flex-wrap items-center gap-3 pt-1">
                  <button 
                    onClick={() => { setActiveTab('policy'); setIsModalOpen(true); }}
                    className="text-xs text-[#C5A46E] hover:underline font-medium inline-flex items-center gap-1 cursor-pointer"
                  >
                    <span>Пълна политика за бисквитки</span>
                    <ChevronRight className="w-3 h-3" />
                  </button>
                  <a
                    href="/poveritelnost"
                    onClick={(e) => {
                      e.preventDefault();
                      window.history.pushState(null, '', '/poveritelnost');
                      window.dispatchEvent(new PopStateEvent('popstate'));
                    }}
                    className="text-xs text-slate-400 hover:text-[#C5A46E] hover:underline font-medium inline-flex items-center gap-1 cursor-pointer"
                  >
                    <span>Политика за лични данни (GDPR)</span>
                    <ChevronRight className="w-3 h-3" />
                  </a>
                </div>
              </div>
            </div>

            {/* Equal Choice Buttons (strictly adhering to EU equal prominence rules) */}
            <div className="flex flex-wrap sm:flex-nowrap items-center gap-2.5 w-full lg:w-auto shrink-0 pt-2 lg:pt-0 border-t lg:border-t-0 border-slate-800">
              <button
                id="cookie-btn-settings"
                onClick={() => { setActiveTab('categories'); setIsModalOpen(true); }}
                className="flex-1 sm:flex-none px-4 py-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700 text-xs font-bold transition flex items-center justify-center gap-1.5 cursor-pointer whitespace-nowrap"
              >
                <Settings className="w-3.5 h-3.5 text-[#C5A46E]" />
                Настройки
              </button>

              <button
                id="cookie-btn-reject-all"
                onClick={handleRejectAll}
                className="flex-1 sm:flex-none px-4 py-2.5 rounded-xl bg-[#15253A] hover:bg-slate-800 text-slate-200 hover:text-white border border-slate-700 text-xs font-bold transition flex items-center justify-center gap-1.5 cursor-pointer whitespace-nowrap"
              >
                <X className="w-3.5 h-3.5 text-rose-400" />
                Откажи всички
              </button>

              <button
                id="cookie-btn-accept-all"
                onClick={handleAcceptAll}
                className="w-full sm:w-auto px-5 py-2.5 rounded-xl bg-[#C5A46E] hover:bg-[#B1905E] text-[#0F1E32] font-black text-xs uppercase tracking-wider transition shadow-lg flex items-center justify-center gap-1.5 cursor-pointer whitespace-nowrap"
              >
                <Check className="w-3.5 h-3.5 stroke-[3]" />
                Приемам всички
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 2. Detailed Cookie Preferences & Policy Modal */}
      {isModalOpen && (
        <div 
          className="fixed inset-0 z-55 bg-slate-950/80 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4 overflow-y-auto animate-in fade-in duration-200"
          role="dialog"
          aria-modal="true"
          aria-labelledby="cookie-modal-title"
        >
          <div className="bg-white rounded-2xl max-w-2xl w-full shadow-2xl border border-slate-100 overflow-hidden font-sans text-slate-700 flex flex-col max-h-[90vh]">
            
            {/* Header */}
            <div className="bg-[#0F1E32] text-white p-5 sm:p-6 flex items-start justify-between gap-4 shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#C5A46E]/20 border border-[#C5A46E]/40 flex items-center justify-center text-lg">
                  🍪
                </div>
                <div>
                  <h3 id="cookie-modal-title" className="text-base sm:text-lg font-bold text-white tracking-tight flex items-center gap-2">
                    Център за поверителност & Бисквитки
                  </h3>
                  <p className="text-xs text-slate-300 font-light mt-0.5">
                    Съгласно Общия регламент за защита на данните (GDPR – Регламент (ЕС) 2016/679)
                  </p>
                </div>
              </div>
              <button
                onClick={() => setIsModalOpen(false)}
                className="text-slate-400 hover:text-white p-1 rounded-lg transition focus:outline-none cursor-pointer"
                aria-label="Затвори"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Navigation Tabs inside modal */}
            <div className="flex border-b border-slate-200 bg-slate-50 px-6 pt-3 gap-2 shrink-0">
              <button
                onClick={() => setActiveTab('categories')}
                className={`pb-3 px-3 text-xs font-bold uppercase tracking-wider border-b-2 transition cursor-pointer ${
                  activeTab === 'categories'
                    ? 'border-[#C5A46E] text-[#0F1E32]'
                    : 'border-transparent text-slate-500 hover:text-slate-800'
                }`}
              >
                Категории & Настройки
              </button>
              <button
                onClick={() => setActiveTab('policy')}
                className={`pb-3 px-3 text-xs font-bold uppercase tracking-wider border-b-2 transition cursor-pointer ${
                  activeTab === 'policy'
                    ? 'border-[#C5A46E] text-[#0F1E32]'
                    : 'border-transparent text-slate-500 hover:text-slate-800'
                }`}
              >
                Политика за бисквитки & Права
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-5 sm:p-6 overflow-y-auto space-y-4 text-xs">
              {activeTab === 'categories' ? (
                <>
                  <p className="text-slate-600 leading-relaxed">
                    Можете да изберете кои категории бисквитки да бъдат активни. Задължителните бисквитки са необходими за работата на сайта и не могат да бъдат изключени.
                  </p>

                  <div className="space-y-3.5 mt-2">
                    {COOKIE_CATEGORIES.map((cat) => {
                      const isRequired = cat.required;
                      const isChecked = isRequired 
                        ? true 
                        : cat.id === 'analytics' 
                          ? prefs.analytics 
                          : cat.id === 'marketing' 
                            ? prefs.marketing 
                            : prefs.functional;

                      const handleToggle = () => {
                        if (isRequired) return;
                        if (cat.id === 'analytics') {
                          setPrefs(prev => ({ ...prev, analytics: !prev.analytics }));
                        } else if (cat.id === 'marketing') {
                          setPrefs(prev => ({ ...prev, marketing: !prev.marketing }));
                        } else if (cat.id === 'functional') {
                          setPrefs(prev => ({ ...prev, functional: !prev.functional }));
                        }
                      };

                      return (
                        <div 
                          key={cat.id}
                          className="bg-slate-50 border border-slate-200/90 rounded-xl p-4 transition-all hover:border-slate-300"
                        >
                          <div className="flex items-start justify-between gap-4">
                            <div className="space-y-1">
                              <div className="flex flex-wrap items-center gap-2">
                                <span className="p-1 rounded-md bg-white border border-slate-200">
                                  {getCategoryIcon(cat.id)}
                                </span>
                                <h4 className="font-bold text-slate-900 text-xs sm:text-sm">
                                  {cat.name}
                                </h4>
                                <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                                  isRequired 
                                    ? 'bg-emerald-100 text-emerald-800' 
                                    : isChecked 
                                      ? 'bg-blue-100 text-blue-800' 
                                      : 'bg-slate-200 text-slate-600'
                                }`}>
                                  {isRequired ? 'Винаги активни' : isChecked ? 'Разрешени' : 'Изключени'}
                                </span>
                              </div>
                              <p className="text-slate-600 text-xs leading-relaxed pt-1">
                                {cat.description}
                              </p>
                              
                              {/* Highlighted services in this category */}
                              <div className="flex flex-wrap gap-1.5 pt-2">
                                {cat.services.map((svc, idx) => (
                                  <span 
                                    key={idx}
                                    className="px-2 py-0.5 bg-white border border-slate-200 text-slate-700 rounded text-[10px] font-medium"
                                  >
                                    {svc}
                                  </span>
                                ))}
                              </div>
                            </div>

                            {/* Toggle Switch */}
                            <div className="shrink-0 pt-0.5">
                              {isRequired ? (
                                <div className="w-11 h-6 bg-emerald-500 rounded-full flex items-center justify-end px-1 opacity-70 cursor-not-allowed">
                                  <div className="w-4 h-4 bg-white rounded-full shadow"></div>
                                </div>
                              ) : (
                                <button
                                  type="button"
                                  role="switch"
                                  aria-checked={isChecked}
                                  onClick={handleToggle}
                                  className={`w-11 h-6 rounded-full transition-colors relative focus:outline-none focus:ring-2 focus:ring-[#C5A46E] cursor-pointer ${
                                    isChecked ? 'bg-[#C5A46E]' : 'bg-slate-300'
                                  }`}
                                >
                                  <span 
                                    className={`block w-4 h-4 bg-white rounded-full shadow transform transition-transform duration-200 ${
                                      isChecked ? 'translate-x-6' : 'translate-x-1'
                                    }`}
                                  />
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              ) : (
                /* Cookie Policy & GDPR Rights Tab */
                <div className="space-y-4 text-xs text-slate-600 leading-relaxed font-sans">
                  <div>
                    <h4 className="font-bold text-slate-900 text-sm mb-1">Какво представляват бисквитките?</h4>
                    <p>
                      Бисквитките са малки текстови файлове, които се запазват на Вашето устройство (компютър, таблет или телефон), когато посещавате уебсайта на Варна Имотен Център. Те ни помагат да направим сайта по-бърз, удобен и сигурен, както и да анализираме потребителския интерес към имотите.
                    </p>
                  </div>

                  <div>
                    <h4 className="font-bold text-slate-900 text-sm mb-1">Интегрирани услуги и платформи</h4>
                    <ul className="list-disc pl-4 space-y-1 mt-1 text-slate-700">
                      <li><strong>Google Analytics 4 (GA4):</strong> Анонимно отчитане на посещения, разгледани имоти и време на престой в съответствие с европейските регламенти за поверителност.</li>
                      <li><strong>Google Search Console:</strong> Анализ на органичния трафик и ефективността на индексиране на страниците.</li>
                      <li><strong>Google Ads:</strong> Измерване на резултатите от реклами за имоти в мрежата за търсене на Google.</li>
                      <li><strong>Meta Pixel (Facebook Pixel):</strong> Анализ на реализациите и показване на подходящи оферти в платформите на Meta.</li>
                      <li><strong>Google Maps Embed:</strong> Интерактивно представяне на адреса на офиса ни във Варна.</li>
                    </ul>
                  </div>

                  <div>
                    <h4 className="font-bold text-slate-900 text-sm mb-1">Вашите права съгласно GDPR (ЕС 2016/679)</h4>
                    <p>
                      Имате право по всяко време да промените или оттеглите съгласието си. Оттеглянето на съгласието става толкова лесно, колкото и предоставянето му — чрез бутона за бисквитки в долния ляв ъгъл на екрана или чрез линка във футъра.
                    </p>
                  </div>

                  <div className="bg-slate-50 border border-slate-200 rounded-xl p-3.5 text-slate-600">
                    <span className="font-bold text-slate-800 block mb-0.5">Администратор на личните данни:</span>
                    <span>„Имотен център Варна“ ЕООД, ЕИК 204164006, гр. Варна, ул. „Генерал Колев“ № 113, ет. 3, офис 3.5.</span>
                    <div className="mt-2">
                      <a 
                        href="/poveritelnost" 
                        onClick={(e) => {
                          e.preventDefault();
                          setIsModalOpen(false);
                          window.history.pushState(null, '', '/poveritelnost');
                          window.dispatchEvent(new PopStateEvent('popstate'));
                        }}
                        className="text-[#C5A46E] hover:underline font-bold text-xs inline-flex items-center gap-1"
                      >
                        Виж пълната Политика за защита на личните данни (GDPR) &rarr;
                      </a>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Modal Actions Footer */}
            <div className="bg-slate-50 border-t border-slate-200 p-4 sm:p-5 flex flex-wrap sm:flex-nowrap items-center justify-between gap-2.5 shrink-0">
              <div className="flex gap-2 w-full sm:w-auto">
                <button
                  type="button"
                  onClick={handleRejectAll}
                  className="flex-1 sm:flex-none px-3.5 py-2 rounded-xl bg-white hover:bg-slate-100 text-slate-700 border border-slate-300 font-bold text-xs transition cursor-pointer"
                >
                  Откажи всички
                </button>
                <button
                  type="button"
                  onClick={handleAcceptAll}
                  className="flex-1 sm:flex-none px-3.5 py-2 rounded-xl bg-slate-800 hover:bg-slate-900 text-white font-bold text-xs transition cursor-pointer"
                >
                  Приеми всички
                </button>
              </div>

              <button
                type="button"
                onClick={handleSaveCustom}
                className="w-full sm:w-auto px-5 py-2.5 rounded-xl bg-[#C5A46E] hover:bg-[#B1905E] text-[#0F1E32] font-black text-xs uppercase tracking-wider transition shadow-md flex items-center justify-center gap-1.5 cursor-pointer"
              >
                <Check className="w-4 h-4 stroke-[3]" />
                Запази моя избор
              </button>
            </div>

          </div>
        </div>
      )}
    </>
  );
};
